
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CountryItem } from '../types';
import { SocialPlatform } from '@/types/socialPlatform';
import { VideoConfig } from '@/types/videoConfig';
import { usePermissions } from '@/hooks/use-permissions';

export const useCountriesData = () => {
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isSuperAdmin, getAssignedCountries } = usePermissions();

  const fetchCountries = async () => {
    try {
      let query = supabase
        .from('countries')
        .select(`
          *,
          regions (
            id,
            name,
            code
          )
        `)
        .order('name');

      // Apply country filtering for non-super admins
      if (!isSuperAdmin()) {
        const assignedCountries = getAssignedCountries();
        if (assignedCountries.length > 0) {
          query = query.in('code', assignedCountries);
        } else {
          // No assigned countries - return empty result
          setCountries([]);
          setIsLoading(false);
          return;
        }
      }
        
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Map the DB response to ensure each item has an id property for UI purposes
      // Cast social_media to SocialPlatform[] and featured_video to VideoConfig to maintain type safety
      const countriesWithIds = data.map(country => ({
        ...country,
        id: country.code, // Use code as id for consistency
        social_media: (country.social_media || []) as unknown as SocialPlatform[],
        featured_video: country.featured_video ? country.featured_video as unknown as VideoConfig : null,
        region_name: country.regions?.name || null,
        region_code: country.regions?.code || null,
      })) as CountryItem[];
      
      setCountries(countriesWithIds);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    countries,
    setCountries,
    isLoading,
    fetchCountries
  };
};
